import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { signInWithCustomToken, onAuthStateChanged, User, ParsedToken } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebaseConfig';
import { useNavigate, useLocation } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';

interface StudentUser extends User {
  leadUid?: string;
  companyId?: string;
}

interface CustomClaims extends ParsedToken {
  leadUid: string;
  companyId: string;
  role: string;
}

interface StudentAuthContextType {
  currentStudent: StudentUser | null;
  studentLogin: (username: string, password: string) => Promise<void>;
  studentLogout: () => Promise<void>;
  loading: boolean;
  studentData: any | null;
}

export const StudentAuthContext = createContext<StudentAuthContextType | undefined>(undefined);

export const StudentAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentStudent, setCurrentStudent] = useState<StudentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [studentData, setStudentData] = useState<any | null>(null);
  const navigate = useNavigate();
  const functions = getFunctions();

  const studentLogin = async (username: string, password: string) => {
    try {
      const createCustomToken = httpsCallable(functions, 'createCustomToken');
      const result = await createCustomToken({ username, password });
      
      const { token, user } = result.data as { token: string; user: any };
      
      // Sign in with the custom token
      await signInWithCustomToken(auth, token);
      
      // Store student-specific data
      setStudentData(user);
      
      localStorage.setItem('isStudentAuthenticated', 'true');
    } catch (error) {
      console.error('Student login error:', error);
      throw error;
    }
  };
  

  const studentLogout = async () => {
    try {
      await auth.signOut();
      localStorage.removeItem('isStudentAuthenticated');
      setCurrentStudent(null);
      setStudentData(null);
      navigate('/student-login');
    } catch (error) {
      console.error('Student logout error:', error);
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          // Get the ID token result to check claims
          const idTokenResult = await user.getIdTokenResult();
          const claims = idTokenResult.claims as CustomClaims;
          
          if (claims.role === 'student') {
            const studentUser = user as StudentUser;
            studentUser.leadUid = claims.leadUid;
            studentUser.companyId = claims.companyId;
            
            // Fetch additional student data if needed
            const studentDocRef = doc(db, 
              `companies/${claims.companyId}/leads/${claims.leadUid}`
            );
            const studentDoc = await getDoc(studentDocRef);
            
            if (studentDoc.exists()) {
              setStudentData(studentDoc.data());
              setCurrentStudent(studentUser);
            } else {
              await studentLogout();
            }
          } else {
            // Not a student user, set states to null without navigation
            setCurrentStudent(null);
            setStudentData(null);
          }
        } else {
          setCurrentStudent(null);
          setStudentData(null);
        }
      } catch (error) {
        console.error('Error in auth state change:', error);
        setCurrentStudent(null);
        setStudentData(null);
      } finally {
        setLoading(false);
      }
    });
  
    return () => unsubscribe();
  }, [navigate]);

  const value = {
    currentStudent,
    studentLogin,
    studentLogout,
    loading,
    studentData
  };

  return (
    <StudentAuthContext.Provider value={value}>
      {!loading && children}
    </StudentAuthContext.Provider>
  );
};

export const useStudentAuth = () => {
  const context = useContext(StudentAuthContext);
  if (context === undefined) {
    throw new Error('useStudentAuth must be used within a StudentAuthProvider');
  }
  return context;
};

export const ProtectedStudentRoute: React.FC<{ 
  children: ReactNode;
}> = ({ children }) => {
  const { currentStudent, loading } = useStudentAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    document.body.scrollTop = 0; // For Safari
    document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
  }, [ location.pathname ]);

  useEffect(() => {
    if (!loading) {
      if (!currentStudent && !localStorage.getItem('isStudentAuthenticated')) {
        // Redirect to login if not authenticated
        navigate('/student-login', { state: { from: location } });
      } else if (currentStudent && location.pathname === '/student-login') {
        // Redirect to dashboard if already authenticated and trying to access login
        navigate('/student-dashboard');
      }
    }
  }, [currentStudent, loading, navigate, location]);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
    </div>;
  }

  return currentStudent ? <>{children}</> : null;
};